import { Button } from '@/atoms/Button';
import { PageTitle } from '@/atoms/PageTitle';
import { AdsBlock } from '@/components/AdsBlock';
import { Card, CardSize, CardType, TagPosition } from '@/components/Card';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useShowMore } from '@/hooks/useShowMore';
import { Layout } from '@/layout';
import { fetchData } from '@/services/fetchData';
import {
  AdType,
  EventPageType,
  EventType,
  Palettes,
  SettingsType,
  Variants,
} from '@/types';
import { parseText } from '@/utils/parseText';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useEffect, useState } from 'react';
import styles from './Events.module.scss';

const Events = ({
  content,
  menu,
  translations,
  common,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { isTablet } = useAdaptiveWidth();
  const numberOfShown = isTablet ? 4 : 8;
  const [adsArr, setAdsArr] = useState<AdType[]>([]);
  const { push } = useEnhancedRouter();

  useEffect(() => {
    const newAdsArr = content.ads
      .sort(() => Math.random() - Math.random())
      .slice(0, 2);
    setAdsArr(newAdsArr);
  }, [content.ads]);

  const {
    items: events,
    getMore: getAllEvents,
    allShown: allEventsShown,
  } = useShowMore(content.events, numberOfShown, 'events');
  const {
    items: promotedEvents,
    getMore: getAllPromoted,
    allShown: allPromotedShown,
  } = useShowMore(content.promotedEvents, 4, 'promotedEvents');

  const handleArchiveClick = () => {
    push('/events-archive');
  };

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: '/events',
              text: common.eventsLink,
            },
          ]}
        />
        <PageTitle text={translations.eventsTitle} />
        <div className={styles.objects}>
          <div className={styles.objectsContent}>
            {events.map((item: EventType) => (
              <Card
                key={item.id}
                picture={item.picture}
                name={item.name}
                code={item.code}
                cardType={CardType.Panel}
                size={CardSize.SM}
                dateFrom={item.dateFrom}
                dateTo={item.dateTo}
                info={item.info}
                tag={item.tag}
                link={`/events/${item.code}`}
                button={item.button}
                tagPosition={TagPosition.Top}
                closed={item.closed}
                noType
                twoRowTitle
                buttonLink={item.buttonLink}
                withButton
                pageMissing={item.pageMissing}
                fullWidth
                duplicateCardLink
                tagLink={item.tagLink}
              />
            ))}
          </div>
          {!allEventsShown && content.events.length > numberOfShown && (
            <Button
              text={common.showMoreButton}
              onClick={getAllEvents}
              variant={Variants.Outline}
              palette={Palettes.Secondary}
              className={styles.more}
            />
          )}
          <Button
            text="Архив мероприятий"
            onClick={handleArchiveClick}
            variant={Variants.Outline}
            palette={Palettes.Pale}
            icon="/svg/arrow.svg"
            className={styles.more}
          />
        </div>
        <AdsBlock ads={isTablet ? adsArr.slice(0, 1) : adsArr} />
        {promotedEvents && promotedEvents.length > 0 && (
          <div className={styles.objects}>
            <h4 className={styles.objectsTitle}>
              {parseText(translations.promotedEventsTitle)}
            </h4>
            <div className={styles.objectsContent}>
              <div className={styles.objectsContent}>
                {promotedEvents.map((item: EventType) => (
                  <Card
                    key={item.id}
                    picture={item.picture}
                    name={item.name}
                    code={item.code}
                    cardType={CardType.Panel}
                    size={CardSize.SM}
                    dateFrom={item.dateFrom}
                    dateTo={item.dateTo}
                    info={item.info}
                    tag={item.tag}
                    link={`/events/${item.code}`}
                    button={item.button}
                    tagPosition={TagPosition.Top}
                    noType
                    closed={item.closed}
                    twoRowTitle
                    buttonLink={item.buttonLink}
                    withButton
                    pageMissing={item.pageMissing}
                    fullWidth
                    duplicateCardLink
                    tagLink={item.tagLink}
                  />
                ))}
              </div>
            </div>

            {!allPromotedShown && content.promotedEvents.length > 4 && (
              <Button
                text={common.showMoreButton}
                onClick={getAllPromoted}
                variant={Variants.Outline}
                palette={Palettes.Secondary}
                className={styles.more}
              />
            )}
          </div>
        )}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const content: EventPageType = await fetchData({
    url: `/event/view`,
    locale,
  });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;

  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/events'],
      common: menu.translations.common,
    },
  };
}

export default Events;
